import { Component, OnInit, ViewEncapsulation, ElementRef, ViewChild, OnDestroy, Type } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import * as _confetti from 'canvas-confetti';
import { ModalController } from '@ionic/angular';
import { AdvancedSetupComponent } from './advanced-setup/advanced-setup';
import { VideoTutorialComponent } from './video-tutorial/video-tutorial';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { Util } from 'src/shared/util/util';
import { Router } from '@angular/router';
import { WindowService } from 'src/shared/providers/window.service';
import { Me } from 'src/providers/me/me';
import { Client } from 'src/providers/client/client';
import { propertyDashboardPath } from 'src/providers/guard/rental-routes.guard';

// Onboarding component imports for navigation
import { PropertyOnboardingComponent } from './property/property-onboarding.component';
import { SelectIntegrationOnboarding } from './select-integration-onboarding/select-integration-onboarding';
import { EnableAutomaticBookingOnboarding } from './enable-automatic-booking-onboarding/enable-automatic-booking-onboarding';
import { SelectAccessNotesOptionOnboarding } from './select-access-notes-option-onboarding/select-access-notes-option-onboarding';
import { AddProsOnboardingComponent } from './add-pros/add-pros.component';
import { SelectFindNewProsOptionOnboarding } from './select-find-new-pros-option-onboarding/select-find-new-pros-option-onboarding';
import { RestockingOnboardingComponent } from './restocking/restocking-onboarding.component';
import { SelectMaintenanceOptionOnboarding } from './select-maintenance-option-onboarding/select-maintenance-option-onboarding';
import { ToDosModalOnboardingComponent } from './to-dos/to-dos.component';
import { EnableGuestSmartNumberOnboarding } from './enable-guest-smart-number-onboarding/enable-guest-smart-number-onboarding';
import { EnableConciergeMessagingOnboarding } from './enable-concierge-messaging-onboarding/enable-concierge-messaging-onboarding';
import { NotifyProsOfChangeOnboarding } from './notify-pros-of-change-onboarding/notify-pros-of-change-onboarding';
import { BillsOnboardingComponent } from './bills/bills.component';
import { AutomateWorkflowsOnboarding } from './automate-workflows-onboarding/automate-workflows-onboarding';
import { CreateConciergeRulesOnboarding } from './create-concierge-rules-onboarding/create-concierge-rules-onboarding';

@Component({
    selector: 'onboarding-goals',
    templateUrl: './onboarding.html',
    styleUrls: ['./onboarding.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class OnboardingComponent implements OnInit, OnDestroy {

  showAdvancedSteps = false;
  loaded = false;
  advancedGoals: any = null;
  submitted = false;
  goals: any[];
  checkedItems: Set<string> = new Set();
  progress = 0;
  selectedTask: any | null = null;
  completedCount = 0;
  expandedGoal: string | null = null;
  videoModalOpen = false;
  currentVideo = '';
  showSuggestions = false;
  automatedPercentage = 0;
  form: FormGroup = new FormGroup({
    task: new FormControl(''),
    tasks: new FormControl([]),
  });
  @ViewChild('confettiContainer') rootRef!: ElementRef;

  boundAddAdvancedStep = this.addAdvancedStep.bind(this);

  // Flow to component mapping for rightSidePanelService navigation
  private readonly flowToComponentMap: { [key: string]: { component: Type<any>; title: string } } = {
    'selectIntegration': { component: SelectIntegrationOnboarding, title: 'Integrate' },
    'enableAutomaticBooking': { component: EnableAutomaticBookingOnboarding, title: 'Auto-Schedule' },
    'selectAccessNotes': { component: SelectAccessNotesOptionOnboarding, title: 'Access Notes' },
    'addPros': { component: AddProsOnboardingComponent, title: 'Add Your Pros' },
    'selectFindNewPros': { component: SelectFindNewProsOptionOnboarding, title: 'Find New Pros' },
    'inventoryAutomation': { component: RestockingOnboardingComponent, title: 'Add Inventory' },
    'selectMaintenance': { component: SelectMaintenanceOptionOnboarding, title: 'Add Assets' },
    'toDos': { component: ToDosModalOnboardingComponent, title: 'Checklists' },
    'enableGuestSmartNumber': { component: EnableGuestSmartNumberOnboarding, title: 'Add Smart Number' },
    'messageAutomation': { component: EnableConciergeMessagingOnboarding, title: 'Message Automation' },
    'notifyProsOfChange': { component: NotifyProsOfChangeOnboarding, title: 'Notify Pros' },
    'bills': { component: BillsOnboardingComponent, title: 'Bills' },
    'automateWorkflows': { component: AutomateWorkflowsOnboarding, title: 'Automate Workflows' },
    'createConciergeRules': { component: CreateConciergeRulesOnboarding, title: 'Create Rules' },
  };

  // Flows that require property to be added first
  private readonly propertyRequiredFlows = ['toDos', 'inventoryAutomation', 'selectMaintenance', 'addPros', 'schedule', 'selectAccessNotes', 'enableAutomaticBooking'];

constructor(
    private modalController: ModalController,
    private storage: TidyStorage,
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    private onboardingProvider: OnboardingProvider,
    private util: Util,
    private router: Router,
    public windowService: WindowService,
    private me: Me,
    private client: Client
  ) {}

  async ngOnInit(): Promise<void> {
    this.rightSidePanelService.setDialogPageTitle('Getting Started');
    this.onboardingProvider.loaded = false;
    this.loaded = false;
    this.goals = await this.storage.retrieve('onboardingGoals');
    this.advancedGoals = await this.storage.retrieve('advancedOnboardingGoals');

    this.sortGoalsByCompleted();
    this.restoreExpandedGoal();
    this.calculateAutomatedPercentage();
    const me = await this.me.load();
    this.showAdvancedSteps = me.customer_account.advanced_onboarding;
    this.loaded = true;
    this.onboardingProvider.loaded = true;
  }

  calculateAutomatedPercentage(): void {
    if (!this.goals) {
      this.automatedPercentage = 0;
      return;
    }
    let totalSteps = 0;
    let completedSteps = 0;
    this.goals.forEach((goal) => {
      if (goal.user_goal_steps && goal.user_goal_steps.length > 0) {
        totalSteps += goal.user_goal_steps.length;
        completedSteps += goal.user_goal_steps.filter((step) => step.state === 'done').length;
      }
    });
    if (totalSteps === 0) {
      this.automatedPercentage = 0;
      return;
    }
    this.automatedPercentage = Math.round((completedSteps / totalSteps) * 100);
  }

  openDemoUrl(): void {
    this.util.openUrl('https://www.tidy.com/demo');
  }

  private restoreExpandedGoal(): void {
    const expandedGoalId = localStorage.getItem('onboarding_expanded_goal_id');
    if (expandedGoalId && this.goals) {
      const goal = this.goals.find(g => g.id?.toString() === expandedGoalId);
      if (goal) {
        goal.isOpened = true;
      }
    }
  }

  sortGoalsByCompleted() {
    this.goals.sort((a, b) => {
      if (a.state === 'done' && b.state !== 'done') {return 1;}
      if (a.state !== 'done' && b.state === 'done') {return -1;}
      return 0;
    });
  }

  async goToStepDetails(step) {
    this.onboardingProvider.previousOnboardingFlow = null;
    try {
      if (step.state === 'done') {
        return;
      }

      // If flow is undefined, look it up from step title (handles cached goals without flow)
      const stepFlow = step.flow || this.onboardingProvider.getStepData(step.title)?.flow;

      await this.storage.save('cameFromOnboarding', true);
      await this.storage.save('onboardingFlow', stepFlow);

      // Handle addRecurringMaintenance - navigate directly to maintenance page
      if (stepFlow === 'addRecurringMaintenance') {
        this.navCtrl.navigateForward('maintenance');
        this.modalController.dismiss();
        return;
      }

      // Check if property needs to be added first
      const needsProperty = await this.checkIfNeedsProperty(stepFlow);
      if (needsProperty) {
        const flowMapping = this.flowToComponentMap[stepFlow];
        // Don't store component class - it can't be serialized to IndexedDB
        // Property component will look up the component from the flow name
        await this.storage.save('dialog-params', {
          redirectAfterProperty: stepFlow,
          redirectTitle: flowMapping?.title
        });
        this.rightSidePanelService.setDialogPageTitle('Add Properties');
        this.rightSidePanelService.navigateTo('onboarding/property', {}, PropertyOnboardingComponent);
        return;
      }

      // Get component mapping for the flow
      const flowMapping = this.flowToComponentMap[stepFlow];
      if (flowMapping) {
        await this.storage.save('dialog-params', { flow: stepFlow });
        this.rightSidePanelService.setDialogPageTitle(flowMapping.title);
        this.rightSidePanelService.navigateTo(`onboarding/${stepFlow}`, {}, flowMapping.component);
        return;
      }

      // Fallback: set legacy state and navigate to page
      this.onboardingProvider.flow = stepFlow;
      this.onboardingProvider.page = step.page;
      this.onboardingProvider.refreshOnboardingContent();

      if (this.router.url === propertyDashboardPath() && (stepFlow === 'selectAccessNotes' || stepFlow === 'createConciergeRules' || stepFlow === 'notifyProsOfChange')) {
        return;
      }
      if (step.page === 'checklists' && !this.windowService.isDesktopRes) {
        step.page = 'to-dos';
      }
      if (step.page === 'my-pros' && !this.windowService.isDesktopRes) {
        step.page = 'properties';
      }
      this.navCtrl.navigateForward(step.page);
      this.modalController.dismiss();
    } catch (error) {
      this.util.showError((error.error && error.error.message) ? error.error.message : error.message, 10000);
    }
  }

  private async checkIfNeedsProperty(flow: string): Promise<boolean> {
    if (!this.propertyRequiredFlows.includes(flow)) {
      return false;
    }
    const hasProperties = await this.onboardingProvider.checkIfHasProperties();
    return !hasProperties;
  }

  async updateGoal(goalId, chosen) {
    try {
      const payload = {
        chosen
      }
      await this.onboardingProvider.updateChosenGoal(payload, goalId);
      await this.getUpdatedGoals();
      this.onboardingProvider.setShowOnboardingInHeader(true);
      this.util.showSuccess('Goals updated');
    } catch (error) {
      this.util.showError((error.error && error.error.message) ? error.error.message : error.message, 10000);
    }
  }

  async addAdvancedStep() {
    this.submitted = true;
    if (!this.form.value.task) {
      return;
    }
    try {
      const payload = {
        title: 'Advanced',
        description: this.form.value.task,
      }
      await this.onboardingProvider.createAdvancedStep(payload);
      await this.getUpdatedGoals();
    } catch (error) {
      this.util.showError((error.error && error.error.message) ? error.error.message : error.message, 10000);
    }
    this.form.patchValue({
      task: '',
    });
  }

  async markAdvancedStepAsDone(step) {
    if (step.state === 'done') {
      return;
    }
    try {
      await this.onboardingProvider.markAdvancedStepAsDone(step.id);
      await this.getUpdatedGoals();
    } catch (error) {
      this.util.showError((error.error && error.error.message) ? error.error.message : error.message, 10000);
    }
  }

  async getUpdatedGoals() {
    await this.onboardingProvider.getGoalsFromServer();
    this.goals = await this.storage.retrieve('onboardingGoals');
    this.advancedGoals = await this.storage.retrieve('advancedOnboardingGoals') || [];
    this.calculateAutomatedPercentage();
  }

  async openAdvancedSetup() {
    const backdrop = document.querySelector('.backdrop');
    if (backdrop) {
      (backdrop as HTMLElement).style.display = 'block';
    }
    const modal = await this.modalController.create({
      component: AdvancedSetupComponent,
      cssClass: 'advanced-setup-modal',
      showBackdrop: true,
      id: 'advanced-setup-modal',
    });
    await modal.present();
    modal.onDidDismiss().then((_data) => {
      if (backdrop) {
        (backdrop as HTMLElement).style.display = 'none';
      }
    });
  }

  async openVideo(goal) {
    await this.storage.save('goal', goal);
    const backdrop = document.querySelector('.backdrop');
    if (backdrop) {
      (backdrop as HTMLElement).style.display = 'block';
    }
    const modal = await this.modalController.create({
      component: VideoTutorialComponent,
      cssClass: 'video-tutorial-modal',
      showBackdrop: true,
      id: 'video-tutorial-modal',
    });
    await modal.present();
    modal.onDidDismiss().then((_data) => {
      if (backdrop) {
        (backdrop as HTMLElement).style.display = 'none';
      }
    });
  }

  ngOnDestroy(): void {
    this.storage.delete('cameFromOnboarding');
  }

}